import { Formik } from 'formik';

import { isMobile } from 'hooks';
import { useAppSelector } from 'store';
import { formatPrice } from 'utils/helpers';
import { selectAuthUser } from 'store/slices/auth';
import { ButtonColors, PaymentMethodType } from 'types';
import {
  Row,
  Col,
  Icon,
  Button,
  DialogType,
  InputField,
  ButtonTypes,
  SelectField,
  CommonStyles,
} from 'components';

import { useRemainderCart } from '../hooks';
import { TemplateDialog } from '../template-dialog';
import { FullAmountButton } from '../full-amount-button';

import { InfoProps, FormValues, ManualCreditProps } from './types';
import { initialValues, cartTypeOptions, getValidationSchema } from './config';

const Info = ({ title, value }: InfoProps) => (
  <div>
    <p>{`Price for ${title}:`}</p>
    <span className="semibold">{formatPrice(value)}</span>
  </div>
);

export const ManualCredit = ({
  amount,
  onAddPaymentMethod,
  isProcessingFeeEnabled,
}: ManualCreditProps) => {
  const mobile = isMobile();
  const authUser = useAppSelector(selectAuthUser);
  const { remainder, isBalanceZero } = useRemainderCart(
    amount,
    isProcessingFeeEnabled
  );

  const companyProcessingFees = authUser?.company?.processingFee ?? 0;

  return (
    <TemplateDialog
      triggerTitle="Credit"
      icon={<Icon.Credit />}
      title="Complete purchase on terminal"
    >
      {(close, _, ButtonWrap) => (
        <Formik<FormValues>
          initialValues={initialValues}
          validationSchema={getValidationSchema(remainder)}
          onSubmit={async (formValues) => {
            const type = PaymentMethodType.Credit;
            const creditAmount = +formValues.amount;
            console.log(
              '[Checkout] added manual credit in payment method',
              formValues
            );

            onAddPaymentMethod({
              ...formValues,
              type,
              amount: creditAmount,
              totalAmount: creditAmount,
              id: `${formValues.amount}-${type}`,
              proccessingFee: +formValues.proccessingFee,
            });
            close();
          }}
        >
          {({ dirty, values, isSubmitting, setFieldValue }) => {
            const setProcessingFee = (price: number) =>
              setFieldValue(
                'proccessingFee',
                (price * (companyProcessingFees / 100)).toFixed(2)
              );

            return (
              <CommonStyles.Form>
                <div>
                  {values.amount && values.proccessingFee && (
                    <Row>
                      <Col>
                        <Info
                          title="customer"
                          value={
                            +values.amount +
                            (isProcessingFeeEnabled
                              ? +values.proccessingFee
                              : 0)
                          }
                        />
                        <Info title="terminal" value={+values.amount} />
                      </Col>
                    </Row>
                  )}

                  <Row>
                    <Col>
                      <InputField
                        number
                        name="last4CC"
                        label="Last 4 CC#"
                        placeholder="Last 4 CC#"
                      />
                    </Col>
                  </Row>

                  <Row>
                    <Col>
                      <SelectField
                        onlyValue
                        name="cardType"
                        label="Card Type"
                        options={cartTypeOptions}
                        placeholder="Select Card Type"
                      />
                    </Col>
                  </Row>

                  <Row>
                    <Col col={6}>
                      <InputField
                        number
                        decimal
                        name="amount"
                        label="Amount"
                        placeholder="Amount"
                        onChange={(e) => setProcessingFee(+e.target.value)}
                      />
                      <FullAmountButton<FormValues>
                        remainder={remainder}
                        isBalanceZero={isBalanceZero}
                        onClick={() => setProcessingFee(remainder)}
                      />
                    </Col>

                    {isProcessingFeeEnabled && (
                      <Col col={6}>
                        <InputField
                          number
                          decimal
                          disabled
                          name="proccessingFee"
                          label="Processing Fee"
                          placeholder="Processing Fee"
                        />
                      </Col>
                    )}
                  </Row>

                  <Row>
                    <Col>
                      <InputField
                        label="Payment Auth#"
                        name="paymentAuthNumber"
                        placeholder="Payment Auth#"
                      />
                    </Col>
                  </Row>
                </div>

                <ButtonWrap type={DialogType.Plain}>
                  <Button
                    text="Cancel"
                    onClick={close}
                    fullWidth={mobile}
                    colorType={ButtonColors.LightBlue}
                  />
                  <Button
                    text="Add"
                    fullWidth={mobile}
                    type={ButtonTypes.Submit}
                    disabled={!dirty || isSubmitting}
                  />
                </ButtonWrap>
              </CommonStyles.Form>
            );
          }}
        </Formik>
      )}
    </TemplateDialog>
  );
};
